import React, { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Paper,
    Typography,
    Button,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import PoplachConfigForm from '../../../components/PoplachConfigForm';
import { MOCK_PRED_TYPES } from '../../mock/poplachMockData';
import type { PoplachConfig } from '../../../api/JsdhNotifikaceService/generated';
import type { useUpdateAdminKonfiguraceMutation } from '../../../api/JsdhNotifikaceService/service/AdminMutations';

// === DisplejTab ===
interface DisplejTabProps {
    updateKonfiguraceMutation: ReturnType<typeof useUpdateAdminKonfiguraceMutation>;
    editingDisplayConfig: PoplachConfig;
    handleDisplayConfigChange: (updater: (prev: PoplachConfig) => PoplachConfig) => void;
    displayConfigDirty: boolean;
    handleSaveDisplayConfig: () => void;
}

const DisplejTab: React.FC<DisplejTabProps> = ({
    updateKonfiguraceMutation,
    editingDisplayConfig,
    handleDisplayConfigChange,
    displayConfigDirty,
    handleSaveDisplayConfig,
}) => {
    // Vlastní náhled — uživatel si nastaví variantu a počty, klikem otevře /poplach?... v novém tabu
    const [customMockVariant, setCustomMockVariant] = useState<'full' | 'growing' | 'empty' | 'long-text'>('full');
    const [customTechnika, setCustomTechnika] = useState<string>('');
    const [customJedu, setCustomJedu] = useState<string>('');
    const [customNejedu, setCustomNejedu] = useState<string>('');
    const [customPosily, setCustomPosily] = useState<string>('');
    const [customMapaLayout, setCustomMapaLayout] = useState<'' | 'misto' | 'trasa' | 'misto-trasa' | 'off'>('');
    const [customPred, setCustomPred] = useState<string>('');
    const [customUdalost, setCustomUdalost] = useState<string>('');
    const [customPopis, setCustomPopis] = useState<string>('');
    const [customUpresneni, setCustomUpresneni] = useState<string>('');
    const [customNocniRezim, setCustomNocniRezim] = useState<'' | 'auto' | 'light' | 'dark'>('light');
    const [customPravidla, setCustomPravidla] = useState<'' | 'true' | 'false'>('');

    // Náhled tlačítko vedle Uložit — otevře /poplach?mock=full s přepisem podle aktuálně editované konfigurace.
    const buildPreviewUrlFromConfig = (cfg: PoplachConfig): string => {
        const params = new URLSearchParams();
        params.set('mock', 'full');
        if (cfg.mapaLayout) params.set('mapaLayout', cfg.mapaLayout);
        if (cfg.nocniRezim) params.set('nocniRezim', cfg.nocniRezim);
        if (cfg.pravidlaVyjezdu?.enabled !== undefined) {
            params.set('pravidla', cfg.pravidlaVyjezdu.enabled ? 'true' : 'false');
        }
        // Mapa zoom konfigurace (relevantní jen pro mapaLayout=misto / misto-trasa)
        if (cfg.mapa?.mode) params.set('mapMode', cfg.mapa.mode);
        if (cfg.mapa?.fixedZoom !== undefined) params.set('fixedZoom', String(cfg.mapa.fixedZoom));
        if (cfg.mapa?.zoom1 !== undefined) params.set('zoom1', String(cfg.mapa.zoom1));
        if (cfg.mapa?.zoom2 !== undefined) params.set('zoom2', String(cfg.mapa.zoom2));
        if (cfg.mapa?.interval !== undefined) params.set('interval', String(cfg.mapa.interval));
        return `/poplach?${params.toString()}`;
    };

    const buildCustomMockUrl = (): string => {
        const params = new URLSearchParams();
        params.set('mock', customMockVariant);
        if (customTechnika.trim()) params.set('technika', customTechnika.trim());
        if (customJedu.trim()) params.set('jedu', customJedu.trim());
        if (customNejedu.trim()) params.set('nejedu', customNejedu.trim());
        if (customPosily.trim()) params.set('posily', customPosily.trim());
        if (customMapaLayout) params.set('mapaLayout', customMapaLayout);
        if (customPred.trim()) params.set('pred', customPred.trim());
        if (customUdalost.trim()) params.set('udalost', customUdalost.trim());
        if (customPopis.trim()) params.set('popis', customPopis.trim());
        if (customUpresneni.trim()) params.set('upresneni', customUpresneni.trim());
        if (customNocniRezim) params.set('nocniRezim', customNocniRezim);
        if (customPravidla) params.set('pravidla', customPravidla);
        return `/poplach?${params.toString()}`;
    };

    return (
        <>
            {/* Poplachová obrazovka */}
            <Paper sx={{ mb: 3 }}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6">Poplachová obrazovka</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Výchozí konfigurace poplachové obrazovky pro přihlášené uživatele bez device tokenu
                        (např. při otevření /poplach z menu nebo přímým odkazem).
                        Pro Raspberry Pi zařízení se konfigurace nastavuje individuálně u každého device tokenu
                        v sekci Zařízení.
                    </Typography>
                    {/* Náhledy mock variant — pro preset vyjezd-2026, otevírají /poplach v novém tabu */}
                    <Accordion sx={{ mt: 2, bgcolor: 'action.hover', boxShadow: 'none', '&:before': { display: 'none' } }} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 0, '& .MuiAccordionSummary-content': { my: 1 } }}>
                            <Box>
                                <Typography variant="subtitle2">
                                    Náhled poplachové obrazovky
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Otestujte si, jak bude poplachová obrazovka vypadat na displeji (Raspberry Pi). Otevře náhled v novém tabu se simulovanými daty (preset Výjezd – rozšířený, bez backendu).
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Button size="small" variant="outlined" component="a" href="/poplach?mock=full" target="_blank" rel="noopener noreferrer">
                                Plný stav
                            </Button>
                            <Button size="small" variant="outlined" component="a" href="/poplach?mock=growing" target="_blank" rel="noopener noreferrer">
                                Postupně přibývá
                            </Button>
                            <Button size="small" variant="outlined" component="a" href="/poplach?mock=empty" target="_blank" rel="noopener noreferrer">
                                Prázdný stav
                            </Button>
                            <Button size="small" variant="outlined" component="a" href="/poplach?mock=long-text" target="_blank" rel="noopener noreferrer">
                                Dlouhé texty
                            </Button>
                        </Box>
                        {/* === Skupina 1: Nastavení displeje (přepíše konfiguraci displeje pro tento náhled) === */}
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                            Nastavení displeje (pro náhled přepíše uloženou konfiguraci)
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Mapa</InputLabel>
                                <Select
                                    value={customMapaLayout}
                                    label="Mapa"
                                    onChange={(e) => setCustomMapaLayout(e.target.value as typeof customMapaLayout)}
                                >
                                    <MenuItem value=""><em>výchozí</em></MenuItem>
                                    <MenuItem value="misto">Místo</MenuItem>
                                    <MenuItem value="trasa">Trasa</MenuItem>
                                    <MenuItem value="misto-trasa">Místo + Trasa</MenuItem>
                                    <MenuItem value="off">Vypnuto</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Noční režim</InputLabel>
                                <Select
                                    value={customNocniRezim || 'light'}
                                    label="Noční režim"
                                    onChange={(e) => setCustomNocniRezim(e.target.value as typeof customNocniRezim)}
                                >
                                    <MenuItem value="light">Vždy denní</MenuItem>
                                    <MenuItem value="dark">Vždy noční</MenuItem>
                                    <MenuItem value="auto">Auto (dle hodin)</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Pravidla výjezdu</InputLabel>
                                <Select
                                    value={customPravidla}
                                    label="Pravidla výjezdu"
                                    onChange={(e) => setCustomPravidla(e.target.value as typeof customPravidla)}
                                >
                                    <MenuItem value=""><em>výchozí</em></MenuItem>
                                    <MenuItem value="true">Vyhodnocovat (zobrazit plombu)</MenuItem>
                                    <MenuItem value="false">Nevyhodnocovat</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {/* === Skupina 2: Simulovaná data (jen pro náhled — neukládají se) === */}
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                            Simulovaná data poplachu (jen pro náhled — neukládají se)
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Varianta</InputLabel>
                                <Select
                                    value={customMockVariant}
                                    label="Varianta"
                                    onChange={(e) => setCustomMockVariant(e.target.value as typeof customMockVariant)}
                                >
                                    <MenuItem value="full">Plný stav</MenuItem>
                                    <MenuItem value="growing">Postupně přibývá</MenuItem>
                                    <MenuItem value="empty">Prázdný stav</MenuItem>
                                    <MenuItem value="long-text">Dlouhé texty</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                size="small"
                                label="Technika"
                                type="number"
                                value={customTechnika}
                                onChange={(e) => setCustomTechnika(e.target.value)}
                                inputProps={{ min: 0, max: 6 }}
                                sx={{ width: 100 }}
                                placeholder="výchozí"
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                size="small"
                                label="JEDU"
                                type="number"
                                value={customJedu}
                                onChange={(e) => setCustomJedu(e.target.value)}
                                inputProps={{ min: 0, max: 30 }}
                                sx={{ width: 100 }}
                                placeholder="výchozí"
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                size="small"
                                label="NEJEDU"
                                type="number"
                                value={customNejedu}
                                onChange={(e) => setCustomNejedu(e.target.value)}
                                inputProps={{ min: 0, max: 30 }}
                                sx={{ width: 100 }}
                                placeholder="výchozí"
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                size="small"
                                label="Posily"
                                type="number"
                                value={customPosily}
                                onChange={(e) => setCustomPosily(e.target.value)}
                                inputProps={{ min: 0, max: 12 }}
                                sx={{ width: 100 }}
                                placeholder="výchozí"
                                InputLabelProps={{ shrink: true }}
                                helperText="další technika"
                            />
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Typ události</InputLabel>
                                <Select
                                    value={customPred}
                                    label="Typ události"
                                    onChange={(e) => setCustomPred(e.target.value)}
                                >
                                    <MenuItem value=""><em>výchozí</em></MenuItem>
                                    {MOCK_PRED_TYPES.map(t => (
                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                size="small"
                                label="Podtyp"
                                value={customUdalost}
                                onChange={(e) => setCustomUdalost(e.target.value)}
                                placeholder="např. Nízké budovy"
                                sx={{ minWidth: 180 }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                size="small"
                                label="Popis"
                                value={customPopis}
                                onChange={(e) => setCustomPopis(e.target.value)}
                                placeholder="vlastní popis události"
                                sx={{ flex: 1, minWidth: 220 }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                size="small"
                                label="Upřesnění"
                                value={customUpresneni}
                                onChange={(e) => setCustomUpresneni(e.target.value)}
                                placeholder="vlastní upřesnění"
                                sx={{ flex: 1, minWidth: 220 }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>

                        {/* Akce + URL preview */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                            <Button
                                size="small"
                                variant="contained"
                                component="a"
                                href={buildCustomMockUrl()}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Otevřít náhled
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic', wordBreak: 'break-all' }}>
                            URL: <code>{buildCustomMockUrl()}</code>
                        </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Box>
                <Box sx={{ px: 2, pb: 2 }}>
                    <PoplachConfigForm config={editingDisplayConfig} onChange={handleDisplayConfigChange} />
                    <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            component="a"
                            href={buildPreviewUrlFromConfig(editingDisplayConfig)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Náhled
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveDisplayConfig}
                            disabled={updateKonfiguraceMutation.isPending || !displayConfigDirty}
                        >
                            {updateKonfiguraceMutation.isPending ? 'Ukládám...' : 'Uložit'}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </>
    );
};

export default DisplejTab;
